import React, { useState } from 'react';
import { Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebase-config';

function AddSystemForm({ onSystemAdded }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: localStorage.getItem('userEmail') || '',
    githubLink: '',
    siteLink: '',
    tags: '',
    rating: 5
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      setImageFile(file);
      setError('');
    }
  };

  const uploadImage = async (file) => {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
    const imageRef = ref(storage, `system_images/${fileName}`);
    
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);
    return downloadURL;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.description.trim()) {
        throw new Error('Description is required');
      }

      let imageUrl = '';
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      // Prepare tags array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Prepare system data
      const currentDate = new Date();
      const systemData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        author: formData.author.trim() || 'Anonymous',
        authorId: localStorage.getItem('uid') || '',
        githubLink: formData.githubLink.trim(),
        siteLink: formData.siteLink.trim(),
        tags: tagsArray,
        rating: parseInt(formData.rating),
        imageUrl: imageUrl,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Add these for immediate display before Firestore timestamps are resolved
        createdAtFallback: currentDate,
        updatedAtFallback: currentDate
      };

      // Add to Firestore
      const docRef = await addDoc(collection(db, 'systems'), systemData);
      
      setSuccess('System added successfully!');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        author: localStorage.getItem('userEmail') || '',
        githubLink: '',
        siteLink: '',
        tags: '',
        rating: 5
      });
      setImageFile(null);
      
      // Clear file input
      const fileInput = document.getElementById('imageFile');
      if (fileInput) fileInput.value = '';

      // Notify parent component with processed data for display
      if (onSystemAdded) {
        // Include the document ID and use fallback dates for immediate display
        const displayReadySystem = { 
          id: docRef.id, 
          ...systemData,
          // Use the fallback dates for immediate display
          createdAt: systemData.createdAtFallback,
          updatedAt: systemData.updatedAtFallback
        };
        onSystemAdded(displayReadySystem);
      }

    } catch (err) {
      console.error('Error adding system:', err);
      setError(err.message || 'Failed to add system. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="add-system-card">
      <Card.Header>
        <h5 className="mb-0">Add New System</h5>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter system title"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Author</Form.Label>
                <Form.Control
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Enter author name"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter system description"
              required
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>GitHub Link</Form.Label>
                <Form.Control
                  type="url"
                  name="githubLink"
                  value={formData.githubLink}
                  onChange={handleInputChange}
                  placeholder="https://github.com/..."
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Site Link</Form.Label>
                <Form.Control
                  type="url"
                  name="siteLink"
                  value={formData.siteLink}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Tags</Form.Label>
                <Form.Control
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="travel, culture, management (comma-separated)"
                />
                <Form.Text className="text-muted">
                  Separate multiple tags with commas
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Rating</Form.Label>
                <Form.Control
                  as="select"
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                >
                  <option value={1}>1 ⭐</option>
                  <option value={2}>2 ⭐⭐</option>
                  <option value={3}>3 ⭐⭐⭐</option>
                  <option value={4}>4 ⭐⭐⭐⭐</option>
                  <option value={5}>5 ⭐⭐⭐⭐⭐</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label>System Image</Form.Label>
            <Form.Control
              type="file"
              id="imageFile"
              accept="image/*"
              onChange={handleImageChange}
            />
            <Form.Text className="text-muted">
              Upload an image for your system (max 5MB)
            </Form.Text>
          </Form.Group>

          <div className="d-grid">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
              size="lg"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Adding System...
                </>
              ) : (
                'Add System'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default AddSystemForm;
